import { Canvas } from "fabric";
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/components/AuthProvider";
import { useValidation } from "./use-save-validation";
import { useAbortController } from "./use-abort-controller";
import { useVersionControl } from "./use-version-control";
import { sanitizeText } from "@/utils/sanitization";
import { CustomCanvas } from "@/types/fabric";

interface UseSaveOperationsProps {
  canvas: Canvas | null;
  title: string;
  currentDrawingId: string | null;
  onDrawingSaved: (id: string) => void;
  onRefreshNeeded?: (refreshFn: () => void) => void;
}

interface DrawingData {
  drawingData: string;
  previewImage: string;
  analysisText: string | null;
  scenarioType: string | null;
}

export class SaveError extends Error {
  constructor(message: string, public type: 'network' | 'version' | 'permission' | 'validation' | 'unknown') {
    super(message);
    this.name = 'SaveError';
  }
}

export const useSaveOperations = ({
  canvas,
  title,
  currentDrawingId,
  onDrawingSaved,
  onRefreshNeeded,
}: UseSaveOperationsProps) => {
  const { user } = useAuth();
  
  const { validateDrawingData, handleSaveError } = useValidation();
  
  const { getSignal } = useAbortController();
  const { checkVersionConflict, updateCanvasVersion, getCanvasVersion, trackDrawingLineage } = useVersionControl();

  const getDrawingData = useCallback((): DrawingData | null => {
    if (!canvas) return null;
    
    try {
      const drawingData = JSON.stringify(canvas.toJSON({
      }));
      
      const previewImage = canvas.toDataURL({
        format: 'png',
        quality: 0.5,
        multiplier: 0.5
      });

      const analysisText = (canvas as CustomCanvas).data?.analysisText || null;
      const scenarioType = (canvas as CustomCanvas).data?.scenarioType || null;

      return { 
        drawingData, 
        previewImage, 
        analysisText, 
        scenarioType 
      };
    } catch (error) {
      console.error("Error creating drawing data:", error);
      throw new SaveError("Unable to prepare drawing data. Please try again.", "validation");
    }
  }, [canvas]);

  const handleSaveOperation = useCallback(async <T>(
    operation: () => Promise<T>,
    retries = 3,
    delay = 1000
  ): Promise<T> => {
    try {
      return await operation();
    } catch (error: any) {
      console.error('Save operation error:', error);
      
      if (!navigator.onLine || error.message?.includes('network')) {
        if (retries > 0) {
          console.log(`Retrying operation (${retries} attempts left) after ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return handleSaveOperation(operation, retries - 1, delay * 1.5); // Exponential backoff
        }
        throw new SaveError("Network connection issue. Please check your internet connection and try again.", "network");
      }
      
      if (error.message?.includes('version') || error.code === 'PGRST116') {
        throw new SaveError("Version conflict detected. This drawing has been modified elsewhere.", "version");
      }
      
      if (error.message?.includes('permission') || error.code === '42501') {
        throw new SaveError("Permission denied. You don't have permission to save this drawing.", "permission");
      }
      
      if (error.message?.includes('validation')) {
        throw new SaveError("Drawing validation failed. Please check your drawing data and try again.", "validation");
      }
      
      throw new SaveError(error.message || "Unknown error occurred", "unknown");
    }
  }, []);

  const saveExistingDrawing = useCallback(async (description: string, tags: string[]) => {
    const validation = validateDrawingData(canvas);
    if (!validation.success) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description: validation.message,
      });
      return false;
    }

    try {
      const currentVersion = getCanvasVersion(canvas);
      console.log("Checking version:", {currentDrawingId, currentVersion});
      
      const versionCheck = await checkVersionConflict(currentDrawingId!, currentVersion);
      
      if (!versionCheck.success) {
        console.warn("Version conflict detected:", versionCheck);
        toast({
          variant: "destructive",
          title: "Save failed",
          description: versionCheck.message,
        });
        return {
          success: false,
          conflictType: versionCheck.conflictType,
          serverVersion: versionCheck.version
        };
      }

      const data = getDrawingData();
      if (!data) return false;

      const sanitizedTitle = sanitizeText(title, 100);
      const sanitizedDescription = sanitizeText(description, 500);

      const signal = getSignal();

      return await handleSaveOperation(async () => {
        const { data: updated, error: updateError } = await supabase
          .from("whiteboard_history")
          .update({
            title: sanitizedTitle,
            description: sanitizedDescription,
            tags,
            drawing_data: data.drawingData,
            preview_image: data.previewImage,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentDrawingId)
          .eq('user_id', user.id)
          .eq('version', currentVersion)
          .select('version, id')
          .abortSignal(signal)
          .single();

        if (updateError) {
          console.error("Database update error:", updateError);
          throw updateError;
        }

        if (updated) {
          if (data.analysisText) {
            try {
              await supabase
                .from("drawing_analyses")
                .upsert({
                  drawing_id: currentDrawingId,
                  user_id: user.id,
                  analysis_text: data.analysisText,
                  procedure_type: data.scenarioType || 'unknown',
                  status: 'completed'
                }, {
                  onConflict: 'drawing_id'
                })
                .abortSignal(signal);
            } catch (analysisError) {
              console.warn("Analysis save error (non-critical):", analysisError);
            }
          }

          updateCanvasVersion(canvas, currentDrawingId!, updated.version);
          
          (canvas as CustomCanvas).data = {
            ...(canvas as CustomCanvas).data,
            analysisText: data.analysisText,
            currentDrawingId: currentDrawingId!,
            version: updated.version,
            isModified: false
          };

          toast({
            title: "Drawing updated",
            description: "Your changes have been saved successfully.",
          });

          if (onRefreshNeeded) {
            onRefreshNeeded(() => {});
          }

          return {
            success: true,
            drawingId: updated.id,
            version: updated.version
          };
        }
        
        return { success: false };
      });
    } catch (error) {
      if (error instanceof SaveError) {
        toast({
          variant: "destructive",
          title: "Save failed",
          description: error.message,
        });
      } else {
        console.error('Error during save operation:', error);
        toast({
          variant: "destructive",
          title: "Save failed",
          description: "An unexpected error occurred. Please try again.",
        });
      }
      return { success: false };
    }
  }, [
    currentDrawingId, 
    getDrawingData, 
    handleSaveOperation, 
    onRefreshNeeded, 
    title, 
    user?.id, 
    canvas, 
    validateDrawingData, 
    checkVersionConflict, 
    getSignal, 
    updateCanvasVersion,
    getCanvasVersion
  ]);

  const saveNewDrawing = useCallback(async (description: string, tags: string[]) => {
    const validation = validateDrawingData(canvas);
    if (!validation.success) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description: validation.message,
      });
      return false;
    }

    try {
      const data = getDrawingData();
      if (!data) return false;

      const sanitizedTitle = sanitizeText(title, 100);
      const sanitizedDescription = sanitizeText(description, 500);

      const signal = getSignal();

      const originalDrawingId = currentDrawingId || ((canvas as CustomCanvas).data && (canvas as CustomCanvas).data.originalDrawingId);

      return await handleSaveOperation(async () => {
        const insertData: any = {
          title: sanitizedTitle,
          description: sanitizedDescription,
          drawing_data: data.drawingData,
          preview_image: data.previewImage,
          tags,
          user_id: user.id,
          version: 1
        };
        
        if (originalDrawingId) {
          insertData.original_drawing_id = originalDrawingId;
        }

        const { data: newDrawing, error: insertError } = await supabase
          .from("whiteboard_history")
          .insert(insertData)
          .select()
          .abortSignal(signal)
          .single();

        if (insertError) {
          console.error("Database insert error:", insertError);
          throw insertError;
        }
        
        if (data.analysisText && newDrawing) {
          try {
            await supabase
              .from("drawing_analyses")
              .insert({
                drawing_id: newDrawing.id,
                user_id: user.id,
                analysis_text: data.analysisText,
                procedure_type: data.scenarioType || 'unknown',
                status: 'completed'
              })
              .abortSignal(signal);
          } catch (analysisError) {
            console.warn("Analysis save error (non-critical):", analysisError);
          }
        }

        if (newDrawing) {
          if (originalDrawingId) {
            trackDrawingLineage(canvas, originalDrawingId);
          }
          
          updateCanvasVersion(canvas, newDrawing.id, 1);
          
          const customCanvas = canvas as CustomCanvas;
          if (customCanvas.data) {
            customCanvas.data = {
              ...customCanvas.data,
              analysisText: data.analysisText,
              currentDrawingId: newDrawing.id,
              version: 1,
              isModified: false,
              originalDrawingId: originalDrawingId
            };
          }
          
          onDrawingSaved(newDrawing.id);
        }

        toast({
          title: "Drawing saved",
          description: "Your drawing has been saved successfully.",
        });
        
        if (onRefreshNeeded) {
          onRefreshNeeded(() => {});
        }

        return { 
          success: true,
          drawingId: newDrawing.id,
          version: 1
        };
      }, 3);
    } catch (error) {
      if (error instanceof SaveError) {
        toast({
          variant: "destructive",
          title: "Save failed",
          description: error.message,
        });
      } else {
        console.error("Error saving new drawing:", error);
        toast({
          variant: "destructive",
          title: "Save failed",
          description: "An unexpected error occurred. Please try again.",
        });
      }
      return { success: false };
    }
  }, [
    getDrawingData, 
    handleSaveOperation, 
    onDrawingSaved, 
    onRefreshNeeded, 
    title, 
    user?.id, 
    canvas, 
    validateDrawingData, 
    getSignal, 
    updateCanvasVersion,
    currentDrawingId,
    trackDrawingLineage
  ]);

  const quickSave = useCallback(async (description: string = "", tags: string[] = []) => {
    if (!user) {
      toast({
        variant: "destructive", 
        title: "Authentication required",
        description: "Please log in to save drawings.",
      });
      return false;
    }
    
    if (currentDrawingId) {
      return saveExistingDrawing(description, tags);
    } else {
      return saveNewDrawing(description, tags);
    }
  }, [currentDrawingId, saveExistingDrawing, saveNewDrawing, user]);

  return {
    saveExistingDrawing,
    saveNewDrawing,
    quickSave
  };
};
